import React, { useEffect, useState } from "react";
import { getBatch, getSortedCollection, matchQuery } from "sprint-common-web";

import { GetOriginatorsListResponse } from "../../../common/api/delivery-api/originators/GetOriginatorsListResponse";
import { useDeliveryApi } from "../../../common/api/delivery-api/useDeliveryApi";
import { OriginatorListingFilters } from "../listing/useOriginatorListingFilters";
import { OriginatorListingContext } from "./OriginatorListingContext";
import { mapToOriginatorListingItem, OriginatorListingItem } from "./OriginatorListingItem";

interface OriginatorListingProviderProps {
    children: React.ReactNode;
}

export const OriginatorListingProvider: React.FC<OriginatorListingProviderProps> = ({
    children
}: OriginatorListingProviderProps) => {
    const { getOriginatorsList } = useDeliveryApi();

    const [allItems, setItems] = useState<OriginatorListingItem[]>([]);
    const [filteredItems, setFilteredItems] = useState<OriginatorListingItem[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [totalItems, setTotalItems] = useState<number>(0);

    useEffect(() => {
        setIsLoading(true);

        getOriginatorsList()
            .then((response: GetOriginatorsListResponse) => {
                const originators = response.items.map(mapToOriginatorListingItem);

                setItems(originators);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const load = (filters: OriginatorListingFilters) => {
        const sortedAndFilteredItems = getSortedAndFilteredItems(allItems, filters);
        const itemsToDisplay = getBatch(sortedAndFilteredItems, filters.pageSize, filters.page);

        setTotalItems(sortedAndFilteredItems.length);
        setFilteredItems(itemsToDisplay);
    };

    const add = (item: OriginatorListingItem) => {
        setItems((prevState) => [...prevState, item]);
        setFilteredItems((prevState) => [item, ...prevState]);
    };

    return (
        <OriginatorListingContext.Provider value={{ items: filteredItems, isLoading, totalItems, allItems, add, load }}>
            {children}
        </OriginatorListingContext.Provider>
    );
};

const getSortedAndFilteredItems = (items: OriginatorListingItem[], filters: OriginatorListingFilters) => {
    const sortedItems = getSortedCollection(items, filters.sortBy, filters.sortOrder);

    return filterItems(sortedItems, filters.name ?? "");
};

const filterItems = (items: OriginatorListingItem[], name: string) =>
    items.filter((item) => matchQuery(item.name, name));
